import type { Step } from 'react-joyride';

import { TourClasses } from '@revelio/core';

import CompositionPageStepCustomTooltip from './composition-page-step-custom-tooltip';

export enum TourStepTitles {
  WELCOME = 'Welcome to Revelio Labs',
  CONTROL_PANEL = 'Control panel',
  PAGES = 'Analytics',
  ANALYTICS_PAGES = 'Pages',
  COMPOSITIONS = 'Compositions',
  ENTITIES = 'Entities',
  FILTERS = 'Filters',
  ADJUST_VIEW = 'Adjust view',
  CUSTOM_DATA = 'Custom Data',
  TALENT_ACQUISITION = 'Talent Acquisition',
}

export enum TourStepPhase {
  Welcome = 'welcome',
  ControlPanel = 'control_panel',
  AnalyticsSubnav = 'analytics_subnav',
  PageTabs = 'page_tabs',
  Compositions = 'compositions',
  Entities = 'entities',
  Filters = 'filters',
  AdjustView = 'adjust_view',
  CustomData = 'custom_data',
  TalentAcquisition = 'talent_acquisition',
}

export function getTourStepPhase(
  step: Pick<Step, 'data'>
): TourStepPhase | undefined {
  const payload = step.data;
  if (
    payload === undefined ||
    typeof payload !== 'object' ||
    payload === null
  ) {
    return undefined;
  }
  if (!('tourPhase' in payload)) {
    return undefined;
  }
  const candidate = payload.tourPhase;
  if (typeof candidate !== 'string') {
    return undefined;
  }

  return Object.values(TourStepPhase).find((phase) => phase === candidate);
}

export type TourStep = Step & {
  legacyContent?: string;
  /** Omitted when the classic dashboard chrome is enabled */
  newDashboardLayoutOnly?: boolean;
  /** Title on classic chrome when different from {@link TourStepTitles} / default copy */
  legacyTitle?: string;
};

export const steps: TourStep[] = [
  {
    title: TourStepTitles.WELCOME,
    content:
      'Explore the world of workforce data to understand any company or market.',
    placement: 'center',
    target: 'body',
    disableBeacon: true,
    showProgress: false,
    data: { tourPhase: TourStepPhase.Welcome },
  },
  {
    title: TourStepTitles.CONTROL_PANEL,
    content: 'Here is your control panel where you can select a category.',
    legacyContent:
      'Here is your control panel where you can select a unit of analysis.',
    placement: 'right',
    spotlightPadding: 4,
    target: `.${TourClasses.TOUR_CONTROL_PANEL_CLASS}`,
    disableBeacon: true,
    data: { tourPhase: TourStepPhase.ControlPanel },
  },
  {
    title: TourStepTitles.PAGES,
    legacyTitle: 'Pages',
    content: 'Select a unit of analysis to explore.',
    legacyContent: 'Each unit of analysis has different pages to explore.',
    placement: 'auto',
    target: `.${TourClasses.TOUR_DATA_SOURCES_CLASS}`,
    styles: {
      options: {
        arrowColor: 'white',
      },
    },
    disableBeacon: true,
    data: { tourPhase: TourStepPhase.AnalyticsSubnav },
  },
  {
    title: TourStepTitles.ANALYTICS_PAGES,
    content: 'Each unit of analysis has different pages to explore.',
    placement: 'bottom',
    target: `.${TourClasses.TOUR_ANALYTICS_VIEW_TABS_CLASS}`,
    disableBeacon: true,
    newDashboardLayoutOnly: true,
    styles: {
      options: {
        arrowColor: 'white',
      },
    },
    data: { tourPhase: TourStepPhase.PageTabs },
  },
  {
    title: TourStepTitles.COMPOSITIONS,
    content: 'This page focuses on comparing up to 6 units of interest',
    placement: 'center',
    target: 'body',
    disableBeacon: true,
    tooltipComponent: CompositionPageStepCustomTooltip,
    data: { tourPhase: TourStepPhase.Compositions },
  },
  {
    title: TourStepTitles.ENTITIES,
    content: 'Select entities to analyze. ',
    placement: 'right-start',
    target: `.${TourClasses.TOUR_TRACKING_CLASS}`,
    disableBeacon: true,
    data: { tourPhase: TourStepPhase.Entities },
  },
  {
    title: TourStepTitles.FILTERS,
    content:
      'Filter those entities by role, seniority, region and other employee characteristics.',
    placement: 'right-start',
    target: `.${TourClasses.TOUR_FILTER_MENU_CLASS}`,
    disableBeacon: true,
    data: { tourPhase: TourStepPhase.Filters },
  },
  {
    title: TourStepTitles.ADJUST_VIEW,
    content: 'Make static comparisons or view trends over time.',
    placement: 'left-start',
    target: `.${TourClasses.TOUR_VIEW_CLASS}`,
    disableBeacon: true,
    showProgress: false,
    data: { tourPhase: TourStepPhase.AdjustView },
  },
  {
    title: TourStepTitles.CUSTOM_DATA,
    content: 'Create custom datasets, reports, and APIs.',
    placement: 'auto',
    target: `.${TourClasses.TOUR_DELIVERABLES_SUB_NAV_CLASS}`,
    event: 'hover',
    styles: {
      options: {
        arrowColor: 'white',
      },
    },
    disableBeacon: true,
    showProgress: false,
    data: { tourPhase: TourStepPhase.CustomData },
  },
  {
    title: TourStepTitles.TALENT_ACQUISITION,
    content:
      'Search for profiles using our advanced querying ability on the industries best data', // TODO: need real copy from Yedidya
    placement: 'center',
    target: 'body',
    disableBeacon: true,
    showProgress: false,
    data: { tourPhase: TourStepPhase.TalentAcquisition },
  },
];
